import React from 'react';
import { observer } from 'mobx-react';
import { Input } from '@canvas-logic/clp-shared-ui';
import styles from './StyleGuideInputs.module.css';

/**
 * Style guide section documenting single-line input states.
 * Based on the Figma "Canvas Universal Configurator" fields page.
 *
 * Figma source:
 * - Fields states: node-id=528-8899
 */

type InputDemoState =
  | 'idle'
  | 'hover'
  | 'active'
  | 'error'
  | 'disabled'
  | 'idle.focus'
  | 'active.focus'
  | 'error.focus';

interface InputPreviewProps {
  state: InputDemoState;
}

const STATE_CLASS_MAP: Partial<Record<InputDemoState, string>> = {
  hover: styles.forceHover,
  active: styles.forceActive,
  'idle.focus': styles.forceFocus,
  'active.focus': styles.forceActiveFocus,
  'error.focus': styles.forceErrorFocus,
};

const ERROR_MESSAGE = 'some error message';

const InputPreview = ({ state }: InputPreviewProps) => {
  const isError = state === 'error' || state === 'error.focus';
  const isDisabled = state === 'disabled';

  return (
    <div className={`${styles.inputPreview} ${STATE_CLASS_MAP[state] ?? ''}`}>
      <Input
        label="Label"
        placeholder="Placeholder"
        disabled={isDisabled}
        withValidation={isError}
        error={isError ? { message: ERROR_MESSAGE } : undefined}
      />
    </div>
  );
};

const LEFT_COLUMN_STATES: InputDemoState[] = ['idle', 'hover', 'active', 'error', 'disabled'];
const RIGHT_COLUMN_STATES: InputDemoState[] = ['idle.focus', 'active.focus', 'error.focus'];

export const StyleGuideInputs: React.FC = observer(function StyleGuideInputs() {
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Inputs</h2>
      </div>

      <div className={styles.demoBlock}>
        <div className={styles.demo}>
          <div className={styles.columns}>
            <div className={styles.column}>
              {LEFT_COLUMN_STATES.map((state) => (
                <div key={state} className={styles.stateRow}>
                  <span className={styles.stateLabel}>{state}</span>
                  <InputPreview state={state} />
                </div>
              ))}
            </div>

            <div className={styles.column}>
              {RIGHT_COLUMN_STATES.map((state) => (
                <div key={state} className={`${styles.stateRow} ${styles.stateRowLabelRight}`}>
                  <InputPreview state={state} />
                  <span className={styles.stateLabel}>{state}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
